import { createApi } from '@reduxjs/toolkit/query/react';
import moment from 'moment';
import baseQueryWithTokenManagement from 'pages/auth/apiSlice';
import { convertStringsToOptionsObject } from '../utils/helpers';

export const ordersApi = createApi({
  tagTypes: ['ORDER', 'ORDER_LIST', 'ORDER_STATUS', 'ORDER_SOURCE', 'ORDER_ITEMS'],
  reducerPath: 'ordersApi',
  baseQuery: baseQueryWithTokenManagement,
  endpoints: (builder) => ({
    orderList: builder.query<any, any>({
      query: (args) => {
        const { size = 10, page = 1, sortField, forDashboard, sortOrder, search, filters } = args;
        const number = page - 1;
        let sortParam = '';
        if (sortField && sortOrder) {
          sortParam = `&sort=${sortField},${sortOrder}`;
        }
        const params = { ...filters, ...(typeof search !== 'undefined' && { search }) };
        if (forDashboard) {
          params.forDashboard = true;
          params.assignment = JSON.parse(localStorage.getItem('userId') || '');
        }
        return {
          url: `/wrd/api/order/list?size=${size}&page=${number}${sortParam}`,
          params: Object.keys(params).length ? params : undefined,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        const orders = response?.data?.content?.[0]?.orders || [];
        const managers = response?.data?.content?.[0]?.managers || {};

        const transformedOrders = orders.map((item: any) => ({
          ...item,
          formattedLastUpdated: moment(item.lastModifiedDate).format('DD.MM.YYYY'),
          formattedCreatedDate: moment(item.createDate).format('DD.MM.YYYY'),
          createdBy: managers[item.createdBy] || null,
          key: item.id,
        }));

        return {
          ...response,
          data: {
            ...response.data,
            content: transformedOrders,
          },
        };
      },
      providesTags: () => ['ORDER_LIST'],
    }),
    orderById: builder.query<any, { id: string }>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/wrd/api/order/${id}/details`,
        };
      },
      transformResponse: (response: any) => {
        return {
          ...response.data,
          isFinalStatus: ['PAID', 'REJECTED', 'CONFIRMED'].includes(response.data.status),
          formattedLastUpdated: moment(response.data.lastModifiedDate).format('DD.MM.YYYY HH:mm:ss'),
          formattedCreatedDate: moment(response.data.createDate).format('DD.MM.YYYY HH:mm:ss'),
        };
      },
      providesTags: () => ['ORDER'],
    }),
    orderOverviewById: builder.query<any, string>({
      query: (id) => {
        return {
          url: `/wrd/api/order/${id}/overview`,
        };
      },
      transformResponse: (response: any) => {
        return {
          ...response.data,
          paymentTerms: {
            ...response.data.paymentTerms,
          },
        };
      },
      // providesTags: () => ['ORDER'],
    }),
    orderItemsById: builder.query<any, string>({
      query: (orderId) => {
        return {
          url: `/wrd/api/order-items/by-order/${orderId}`,
        };
      },
      transformResponse: (response: any) => {
        const data = response?.data || {};
        const { brands = [], items = [] } = data;
        data.items = items.map((order: any) => ({
          ...order,
          brand: brands.find((brand: any) => brand.id === order.brandId),
        }));
        return {
          ...response.data,
        };
      },
      providesTags: () => ['ORDER_ITEMS'],
    }),
    orderStatusTypes: builder.query<any, void>({
      query: () => {
        return {
          url: `/wrd/api/order/order-status-types`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        response.data = convertStringsToOptionsObject(response.data);
        return response.data;
      },
      providesTags: () => ['ORDER_STATUS'],
    }),
    orderStatusChange: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/order/status',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['ORDER', 'ORDER_LIST', 'ORDER_STATUS', 'ORDER_ITEMS'],
    }),
    createOrder: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/order',
        method: 'post',
        body: data,
      }),
      invalidatesTags: () => ['ORDER_LIST'],
    }),
    updateOrder: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/order',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['ORDER', 'ORDER_LIST'],
    }),
    updateOrderManagement: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/order/management',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['ORDER', 'ORDER_LIST'],
    }),
    cancelOrder: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/order/cancel',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['ORDER', 'ORDER_LIST'],
    }),
    addOrderItem: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/order/add-item',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['ORDER'],
    }),
    updateOrderItem: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/order/update-item',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['ORDER'],
    }),
    removeOrderItem: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/order/remove-item',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['ORDER'],
    }),
  }),
});

export const {
  useOrderListQuery,
  useOrderByIdQuery,
  useOrderOverviewByIdQuery,
  useCreateOrderMutation,
  useOrderItemsByIdQuery,
  useUpdateOrderMutation,
  useUpdateOrderManagementMutation,
  useOrderStatusTypesQuery,
  useOrderStatusChangeMutation,
  useCancelOrderMutation,
  useAddOrderItemMutation,
  useUpdateOrderItemMutation,
  useRemoveOrderItemMutation,
} = ordersApi;
